// Sign-in and sign-up. Supabase email and password (or a magic link), or (self-host, dev) an API key. Sign-up takes an
// access code, or nothing when the server has it open (F8): then a Turnstile check goes along and the address is
// confirmed by mail ("Check your email"). Dark, the promise on the left, a still of the work on the right, one white
// primary action.

import { type FormEvent, useEffect, useState } from "react";
import still from "../../../../docs/brand/stills/web/golden-hour-stage-door.jpg";
import {
  type AuthMode,
  fetchCreditEur,
  isApiKey,
  saveApiKey,
  sendMagicLink,
  signInWithPassword,
  signUp,
} from "../auth.ts";
import { BRAND, loadBrandFonts, loginVideoWanted } from "../brand.ts";
import { sharedConsent } from "../consent.ts";
import { adoptTermsVersion, getLegal } from "../data/legal.ts";
import { getPricing, moneyText } from "../data/money.ts";
import { CORE } from "../departments.ts";
import { pendingInvite } from "../invite.ts";
import {
  CHECK_EMAIL_TITLE,
  checkEmailText,
  createAccount,
  EXISTS_NOTICE,
  isSignupsClosed,
  PASSWORD_MAX,
  PASSWORD_MIN,
  SignupError,
  signInErrorText,
  starterLine,
} from "../signup.ts";
import { Mark } from "../ui/icons.tsx";
import { Spectrum } from "../ui/kit.tsx";
import { Turnstile } from "./Turnstile.tsx";
import "./login.css";

/** The site's "Start free" and "Get seats" link to `app.filmsquare.ai/?signup`: open on the create-account form. */
export function wantsSignup(
  search: string = typeof location === "undefined" ? "" : location.search,
): boolean {
  return new URLSearchParams(search).has("signup");
}

export function Login({
  mode,
  onKey,
  invited = false,
  connecting = false,
}: {
  mode: AuthMode;
  onKey: () => void;
  /** an invitation link is waiting; say so, because it continues after sign-in */
  invited?: boolean;
  /** Claude asked to connect (a consent link is waiting); it continues after sign-in */
  connecting?: boolean;
}) {
  const [value, setValue] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState<"idle" | "busy" | "sent" | "confirm">("idle");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // sign in (the default) or create an account with an access code / the pending invitation
  const [creating, setCreating] = useState(
    () => mode === "supabase" && wantsSignup() && (!BRAND.inviteOnly || invited || !!pendingInvite.get()),
  );
  const [code, setCode] = useState("");
  // the server refused the pending invitation (expired, used, not for this address): this screen stops sending it
  const [refusedToken, setRefusedToken] = useState<string | null>(null);
  const [, rerender] = useState(0);
  useEffect(() => loadBrandFonts(), []);
  const [showVideo] = useState(loginVideoWanted);
  useEffect(() => {
    const on = () => rerender((n) => n + 1);
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  // the Terms of Service and Privacy Policy, agreed to before an account is made (sent as `acceptTerms`)
  const [agreed, setAgreed] = useState(false);
  const legal = getLegal();
  const [starter, setStarter] = useState(0);
  const [creditEur, setCreditEur] = useState(0.05);
  // an invitation in this tab is the way in: no access code needed
  const inviteRefused = refusedToken !== null && refusedToken === pendingInvite.get();
  const hasInvite = !inviteRefused && (invited || !!pendingInvite.get());
  // what the server allows (GET /pricing, loaded before this screen): open sign-up and the Turnstile check
  const hosted = getPricing().hosted;
  const open = hosted?.signup === "open";
  const siteKey = hosted?.turnstileSiteKey ?? null;
  const [challenge, setChallenge] = useState<string | null>(null);
  const [challengeReset, setChallengeReset] = useState(0);
  const [challengeDown, setChallengeDown] = useState(false);
  const showChallenge = creating && !!siteKey;
  useEffect(() => {
    if (starter > 0) void fetchCreditEur().then(setCreditEur);
  }, [starter]);

  const switchTo = (create: boolean, say: string | null = null) => {
    setCreating(create);
    setError(null);
    setNotice(say);
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (mode === "apikey") {
      if (!isApiKey(value)) return setError("An API key starts with fsk_");
      saveApiKey(value);
      return onKey();
    }
    setState("busy");
    const sentToken = inviteRefused ? null : pendingInvite.get();
    try {
      if (creating) {
        const out = await createAccount(
          {
            signUp: (e, c, t, p, turnstileToken, acceptTerms) =>
              signUp(e, c, t, p, { turnstileToken, acceptTerms }),
            signIn: signInWithPassword,
            onCreated: (n) => {
              setStarter(n);
              sharedConsent().track("signed_up");
            },
          },
          {
            email: value,
            password,
            code: hasInvite ? "" : code,
            inviteToken: sentToken,
            mode: open ? "open" : "code",
            turnstileToken: showChallenge ? challenge : null,
            acceptTerms: agreed ? legal.terms.version : null,
          },
        );
        if (out.kind === "exists") {
          setState("idle");
          return switchTo(false, EXISTS_NOTICE);
        }
        if (out.kind === "check_email") {
          // nobody is signed in: the mailed link confirms the address and signs in
          setState("confirm");
          setStarter(0);
          return;
        }
        // signed in: the app takes over from here
        return;
      }
      await signInWithPassword(value.trim(), password);
    } catch (err) {
      setState("idle");
      setStarter(0);
      if (err instanceof SignupError && err.code === "terms_required") {
        // the terms moved on since this page loaded: take up the version in force and ask again
        adoptTermsVersion(err.details);
        setAgreed(false);
      }
      if (
        creating &&
        sentToken !== null &&
        err instanceof SignupError &&
        err.code === "access_code_required"
      ) {
        setRefusedToken(sentToken);
        setError(
          "Your invitation link no longer works for this address. Check the address, or enter an access code.",
        );
      } else {
        setError(signInErrorText(err));
      }
      // a Turnstile token works once: ask for a new one
      setChallenge(null);
      setChallengeReset((k) => k + 1);
    }
  }

  /** Sign in without a password: for people who have none yet, such as the owner. */
  async function emailLink() {
    setError(null);
    setNotice(null);
    if (!value.trim()) return setError("Enter your email first.");
    setState("busy");
    try {
      await sendMagicLink(value.trim());
      setState("sent");
    } catch (err) {
      setState("idle");
      if (isSignupsClosed(err)) {
        if (BRAND.inviteOnly && !hasInvite)
          return setError("There is no account for that address yet. Ask your producer for an invitation.");
        return switchTo(true, "There is no account for that address yet. Create one to get in.");
      }
      setError(signInErrorText(err));
    }
  }

  return (
    <main className={BRAND.login ? `login login--${BRAND.id}` : "login"}>
      <section className="login-panel">
        <h1 className="login-brand">
          {BRAND.login ? (
            <img src={BRAND.login.logo} alt={BRAND.name} width={120} height={26} />
          ) : (
            <>
              <Mark size={24} />
              {BRAND.name}
            </>
          )}
        </h1>
        <div className="login-body">
          <p className="login-promise">
            {BRAND.promise[0]}
            <br />
            {BRAND.login ? <em>{BRAND.promise[1]}</em> : BRAND.promise[1]}
          </p>
          {!BRAND.login && <Spectrum height={2} />}
          {invited && !inviteRefused && (
            <p className="login-invite">
              Sign in, or create an account, to accept your invitation. It opens right after.
            </p>
          )}
          {connecting && !invited && (
            <p className="login-invite">Sign in to connect Claude. You choose what it may do right after.</p>
          )}
          {state === "confirm" ? (
            <div className="login-sent" role="status">
              <p className="login-sent-title">{CHECK_EMAIL_TITLE}</p>
              <p>{checkEmailText(value.trim())}</p>
              <div className="login-sent-alt">
                <button
                  type="button"
                  className="k-btn k-text"
                  onClick={() => {
                    setState("idle");
                    switchTo(false);
                  }}
                >
                  Already confirmed? Sign in
                </button>
                <button type="button" className="k-btn k-text" onClick={() => setState("idle")}>
                  Use another email
                </button>
              </div>
            </div>
          ) : state === "sent" ? (
            <div className="login-sent" role="status">
              <p className="login-sent-title">Check your inbox</p>
              <p>
                We sent a sign-in link to <strong>{value.trim()}</strong>. Open it on this device; it signs
                you in here.
              </p>
              <button type="button" className="k-btn k-text" onClick={() => setState("idle")}>
                Use another email
              </button>
            </div>
          ) : (
            <form onSubmit={submit} noValidate={mode === "apikey"}>
              {mode === "supabase" ? (
                <>
                  <label htmlFor="email">Work email</label>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="you@studio.com"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    aria-invalid={!!error || undefined}
                    aria-describedby={error ? "login-error" : "login-hint"}
                  />
                  {creating && !hasInvite && (
                    <>
                      <label htmlFor="access-code">{open ? "Access code (optional)" : "Access code"}</label>
                      <input
                        id="access-code"
                        className="login-code"
                        type="text"
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        required={!open}
                        placeholder="FS-XXXX-XXXX"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                      />
                    </>
                  )}
                  <label htmlFor="password">{creating ? "Choose a password" : "Password"}</label>
                  <input
                    id="password"
                    type="password"
                    autoComplete={creating ? "new-password" : "current-password"}
                    required
                    minLength={creating ? PASSWORD_MIN : undefined}
                    maxLength={PASSWORD_MAX}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    aria-invalid={!!error || undefined}
                    aria-describedby={error ? "login-error" : "login-hint"}
                  />
                  {showChallenge && siteKey && (
                    <Turnstile
                      siteKey={siteKey}
                      resetKey={challengeReset}
                      onToken={setChallenge}
                      onUnavailable={() => setChallengeDown(true)}
                    />
                  )}
                  {showChallenge && challengeDown && (
                    <p className="err" role="alert">
                      The check that you are a person could not load. Reload the page, or turn off a blocker
                      for this site.
                    </p>
                  )}
                  {creating && (
                    <label className="login-agree">
                      <input
                        type="checkbox"
                        required
                        checked={agreed}
                        onChange={(e) => setAgreed(e.target.checked)}
                      />
                      <span>
                        I agree to the{" "}
                        <a className="link" href={legal.terms.url} target="_blank" rel="noopener noreferrer">
                          Terms of Service
                        </a>{" "}
                        and have read the{" "}
                        <a
                          className="link"
                          href={legal.privacy.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Privacy Policy
                        </a>
                      </span>
                    </label>
                  )}
                  <button
                    type="submit"
                    className="k-btn k-primary k-lg"
                    disabled={state === "busy" || (showChallenge && !challenge) || (creating && !agreed)}
                  >
                    {state === "busy"
                      ? creating
                        ? starter > 0
                          ? "Signing you in…"
                          : "Creating…"
                        : "Signing in…"
                      : creating
                        ? "Create account"
                        : "Sign in"}
                  </button>
                  {starter > 0 && (
                    <p className="login-starter" role="status">
                      Account created. {starterLine(starter, creditEur)}
                    </p>
                  )}
                  <p className="login-hint" id="login-hint" hidden={!!BRAND.login && !creating}>
                    {creating
                      ? `${
                          hasInvite
                            ? "Your invitation lets you in."
                            : open
                              ? `Free to start: ${moneyText(hosted?.free.credits ?? 0, "long")}, no card. Have an access code? It adds extra credits.`
                              : `New to ${BRAND.name}? Enter the access code from your invitation.`
                        } Use at least ${PASSWORD_MIN} characters for the password.${
                          open && !hasInvite ? " We email you a link to confirm your address." : ""
                        }`
                      : "Sign in with your email and password."}
                  </p>
                  <div className="login-alt">
                    {creating ? (
                      <button type="button" className="k-btn k-text" onClick={() => switchTo(false)}>
                        Already have an account? Sign in
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="k-btn k-text"
                          disabled={state === "busy"}
                          onClick={() => void emailLink()}
                        >
                          Email me a sign-in link instead
                        </button>
                        {(!BRAND.inviteOnly || hasInvite) && (
                          <button type="button" className="k-btn k-text" onClick={() => switchTo(true)}>
                            New here? Create an account
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <label htmlFor="key">API key</label>
                  <input
                    id="key"
                    type="password"
                    autoComplete="off"
                    placeholder="fsk_…"
                    required
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    aria-invalid={!!error || undefined}
                    aria-describedby={error ? "login-error" : "login-hint"}
                  />
                  <button type="submit" className="k-btn k-primary k-lg">
                    Sign in
                  </button>
                  <p className="login-hint" id="login-hint">
                    Self-hosted studio: paste a key from your admin. It stays in this browser only.
                  </p>
                </>
              )}
              {notice && !error && (
                <p className="login-notice" role="status">
                  {notice}
                </p>
              )}
              {error && (
                <p className="err" id="login-error" role="alert">
                  {error}
                </p>
              )}
            </form>
          )}
        </div>
        <p className="login-foot">{BRAND.login?.foot ?? "Every department. Every model. One production."}</p>
      </section>
      <section className="login-art" aria-hidden="true">
        {showVideo && BRAND.login ? (
          <video autoPlay muted playsInline poster={BRAND.login.art}>
            {BRAND.login.video.map((v) => (
              <source key={v.src} src={v.src} type={v.type} />
            ))}
          </video>
        ) : (
          <img src={BRAND.login?.art ?? still} alt="" />
        )}
        {!BRAND.login && (
          <ul className="login-strip">
            {CORE.map((d, i) => (
              <li
                key={d.id}
                style={{
                  background: i < 6 ? `var(--dept-${d.id})` : undefined,
                  borderColor: `var(--dept-${d.id})`,
                }}
              />
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
